import React, { createContext, useContext, useEffect, useState } from 'react';

interface Subscription {
  id: string;
  hotel_id: string;
  plan_id: string;
  status: 'trial' | 'active' | 'expired' | 'suspended';
  start_date: string;
  end_date?: string;
  auto_renew: boolean;
  trial_ends_at?: string;
}

interface Module {
  id: string;
  code: string;
  name: string;
  description: string;
  is_core: boolean;
  is_active: boolean;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  modules: Module[];
  loading: boolean;
  error: string | null;
  hasModule: (moduleCode: string) => boolean;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  modules: [],
  loading: false,
  error: null,
  hasModule: () => false,
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          // No token — skip subscription fetch, leave modules empty (PremiumGate will hide premium items)
          setLoading(false);
          return;
        }
        const { get } = await import('../utils/httpClient');

        const [subRes, modRes] = await Promise.allSettled([
          get('/api/v1/subscriptions/current'),
          get('/api/v1/modules/available'),
        ]);

        if (subRes.status === 'fulfilled' && subRes.value.ok) {
          const subData = await subRes.value.json();
          // status "none" means no plan yet — keep subscription null for UI
          if (subData && subData.status !== 'none') {
            setSubscription(subData);
          }
        }
        // 401/404/network: silent — not an error banner case

        if (modRes.status === 'fulfilled' && modRes.value.ok) {
          const modData = await modRes.value.json();
          if (Array.isArray(modData)) {
            setModules(modData);
          }
        }
        // If modules fetch fails, modules stays empty — PremiumGate will hide premium nav items
      } catch {
        // Silent: subscription is optional for core hotel operations
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, []);

  const hasModule = (moduleCode: string): boolean => {
    return modules.some((m) => m.code === moduleCode && m.is_active);
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, modules, loading, error, hasModule }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => useContext(SubscriptionContext);

export default SubscriptionContext;
